// Account-level chat state shared by /chats and /account: blocked people,
// the server's capabilities (terms version) and the user's chat switches.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api, unwrap } from "../api";

export const BLOCKS_KEY = ["me", "blocks"] as const;

export function useCapabilities() {
  return useQuery({
    queryKey: ["capabilities"],
    queryFn: async () => unwrap(await api.GET("/capabilities")),
    staleTime: Infinity,
  });
}

/** The people this user has blocked, and their ids as a set. */
export function useBlocks() {
  const blocks = useQuery({
    queryKey: BLOCKS_KEY,
    queryFn: async () => unwrap(await api.GET("/me/blocks")),
  });
  const ids = useMemo(() => new Set((blocks.data ?? []).map((b) => b.id)), [blocks.data]);
  return { blocks, ids };
}

/** Blocks (`blocked: true`) or unblocks someone. */
export function useSetBlocked() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, blocked }: { userId: string; blocked: boolean }) => {
      const params = { params: { path: { userId } } };
      // 204 has no body for `unwrap`; only failures matter.
      const result = blocked
        ? await api.PUT("/me/blocks/{userId}", params)
        : await api.DELETE("/me/blocks/{userId}", params);
      if (!result.response.ok) unwrap({ ...result, data: undefined });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: BLOCKS_KEY }),
  });
}
